<template>
    <div class="radar-container">
        <div class="radar-container__inner">
            <template v-if="hasRadarContent && !getIsLoading">
                <!-- RADAR 'CRITICAL CARDS' -->
                <RadarCriticalCards v-if="criticalCards.length > 0" :criticalCards="criticalCards" :columnCount="criticalCardsColumnCount" @card-click="handleRadarNavigation" />
                <!-- RADAR 'MODULES' -->
                <RadarModules v-if="modules.length > 0" :modules="modules" :columnCount="modulesColumnCount" @item-click="handleRadarNavigation" />
            </template>
            <EmptyPage v-else-if="showEmptyState" customClass="radar-empty-state" v-bind:title="$t('radar.empty_state.title')" v-bind:description="$t('radar.empty_state.description')" />

            <IconLabelButton class="radar-update-button" icon="i-andy-refresh-2" label="sensor.refresh" backgroundColor="#ffffff" textColor="#171718" :disabled="isRefreshDisabled" @click="handleRefresh" />
        </div>
    </div>
</template>

<script>
import EmptyPage from '@/components/layout/EmptyPage.vue'
import RadarCriticalCards from '@/components/domain/radar/RadarCriticalCards.vue'
import RadarModules from './RadarModules.vue'
import IconLabelButton from '@/components/domain/ui/IconLabelButton.vue'
import { TOOLS } from '@/constants.js'
import { resolveCurrentUserRoleContext } from '@/store/utils/templatePermissions.js'
import { resolveColumnCountForWidth, resolveEffectiveColumnCount, resolveInitialColumnCount } from './radarLayout.js'

const REFRESH_COOLDOWN_MS = 60 * 1000

export default {
    name: 'Radar',
    components: {
        EmptyPage,
        RadarCriticalCards,
        RadarModules,
        IconLabelButton
    },
    data() {
        return {
            canRefresh: true,
            refreshTimer: null,
            isLeavingView: false,
            viewportColumnCount: resolveInitialColumnCount()
        }
    },
    computed: {
        radarViewData() {
            return this.$store.getters['radar/getRadarViewData'] || {}
        },
        criticalCards() {
            const criticalCards = this.radarViewData.criticalCards

            return Array.isArray(criticalCards) ? criticalCards : []
        },
        modules() {
            const modules = this.radarViewData.modules

            return Array.isArray(modules) ? modules : []
        },
        // Solo exigimos templates de herramientas habilitadas en la tienda: una herramienta deshabilitada nunca
        // llega a cargar sus templates (ver Default-Layout.vue), así que exigirla siempre dejaría Radar cargando para siempre.
        // Se comprueba el flag "cargado" de cada módulo (no la existencia de templates): una cuenta puede tener
        // la herramienta habilitada sin ninguna plantilla creada, y eso es un estado válido, no "aún cargando".
        areTemplatesLoaded() {
            const enabledToolCodes = this.$store.getters['login/getEnabledToolCodes'] || []

            const templatesLoadedByToolCode = {
                [TOOLS.CHECKLISTS]: this.$store.getters['tasks/getTemplatesLoaded'],
                [TOOLS.REGISTERS]: this.$store.getters['register/getTemplatesLoaded'],
                [TOOLS.ISSUES]: this.$store.getters['issues/getTemplatesLoaded'],
                [TOOLS.AUDITS]: this.$store.getters['audit/getTemplatesLoaded'],
                [TOOLS.ACTION_PLANS]: this.$store.getters['actionplan/getTemplatesLoaded']
            }

            return Object.keys(templatesLoadedByToolCode).every((toolCode) => {
                return !enabledToolCodes.includes(Number(toolCode)) || templatesLoadedByToolCode[toolCode]
            })
        },
        // El filtrado por permisos (canViewRadarTemplateEntry) "falla abierto" (no filtra) si aún no hay
        // employee/rol resueltos, así que Radar también debe esperar a ese contexto antes de pintar KPIs.
        isRoleContextLoaded() {
            return !!resolveCurrentUserRoleContext(this.$store.getters)
        },
        // Debe seguir "cargando" mientras Radar, los templates o el contexto de rol necesarios para filtrar por permisos no estén listos
        getIsLoading() {
            return this.$store.getters['radar/getIsLoading'] || !this.areTemplatesLoaded || !this.isRoleContextLoaded
        },
        hasRadarContent() {
            return this.criticalCards.length > 0 || this.modules.length > 0
        },
        // Columnas efectivas de módulos (sin vacías), compartidas con critical cards para alinear
        modulesColumnCount() {
            return resolveEffectiveColumnCount(this.viewportColumnCount, this.modules.length)
        },
        criticalCardsColumnCount() {
            if (this.criticalCards.length === 1 && this.modules.length === 1) {
                return 1
            }

            if (this.modules.length > 0) {
                return this.modulesColumnCount
            }

            return this.viewportColumnCount
        },
        showEmptyState() {
            return !this.getIsLoading && !this.hasRadarContent
        },
        showRadarUpdateError() {
            return this.$store.getters['radar/getShowRadarUpdateError']
        },
        isRefreshDisabled() {
            return !this.canRefresh || this.getIsLoading
        }
    },
    watch: {
        showRadarUpdateError(hasError, hadError) {
            if (hasError) {
                this.showUpdateErrorSnackbar()
            } else if (hadError) {
                this.$snackbar.close()
            }
        },
        getIsLoading(newVal) {
            if (newVal) {
                this.$overlay.loading()
            } else {
                this.$overlay.close()
            }
        }
    },
    methods: {
        updateViewportColumnCount() {
            this.viewportColumnCount = resolveColumnCountForWidth(window.innerWidth)
        },
        handleRadarNavigation(item) {
            this.$store.dispatch('radar/navigateToKpi', item)
        },
        showUpdateErrorSnackbar() {
            this.$snackbar.error({
                message: this.$t('radar.update_error'),
                duration: 5000,
                closeable: true,
                customClass: 'radar-update-error',
                onClose: () => {
                    // Al abandonar la vista no limpiamos el estado: el error persiste hasta
                    // que una recarga tenga éxito o el usuario lo descarte estando en Radar.
                    if (this.isLeavingView) return

                    this.$store.commit('radar/setShowRadarUpdateError', false)
                }
            })
        },
        async handleRefresh() {
            if (!this.canRefresh || this.getIsLoading) return

            this.canRefresh = false

            this.refreshTimer = setTimeout(() => {
                this.canRefresh = true
            }, REFRESH_COOLDOWN_MS)

            await this.$store.dispatch('radar/loadRadarData', { force: true })
        }
    },
    created() {
        window.addEventListener('resize', this.updateViewportColumnCount)
    },
    mounted() {
        // La carga puede fallar desde el layout antes de abrir Radar, dejando la bandera
        // ya activa al montar: mostramos el error inmediatamente en ese caso.
        if (this.showRadarUpdateError) {
            this.showUpdateErrorSnackbar()
        }

        // uncomment only for frontend testing
        // this.$store.dispatch('radar/loadRadarData')
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateViewportColumnCount)

        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer)
        }

        if (this.showRadarUpdateError) {
            // Cerramos solo el snackbar visible, sin resetear la bandera de error:
            // /radar sigue sin actualizarse, así que debe volver a mostrarse al reabrir Radar.
            this.isLeavingView = true
            this.$snackbar.close()
        }
    }
}
</script>

<style lang="scss" scoped>
.radar-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    background: #f4f2ed;

    &__inner {
        // min-height 100%: si el contenido es corto, el flex ocupa el viewport y
        // margin-top: auto del botón lo baja a la derecha. Si hay scroll, el inner
        // crece con el contenido y el botón queda al final del scroll.
        box-sizing: border-box;
        min-height: 100%;
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 16px;
    }

    // flex: 1 (no height: 100%): el % no resuelve contra un padre con solo min-height.
    // Así el empty rellena el viewport restante del flex y se centra; con contenido el
    // __inner sigue creciendo por min-height + margin-top: auto del botón.
    ::v-deep .empty-container.radar-empty-state {
        flex: 1 1 auto;
        height: auto;
        min-height: 0;

        .empty-page-content {
            width: 100%;
            max-width: 500px;
        }
    }

    ::v-deep .empty-container.radar-empty-state.portrait {
        flex: 1 1 auto;
        height: auto;
        min-height: 0;

        .empty-page-content {
            width: 100% !important;
            max-width: 336px;
        }

        .content-message {
            border-radius: 14px;
            padding: 24px 32px;
        }

        .title-content {
            font-size: 24px;
            line-height: 28px;
        }

        .description {
            font-size: 20px;
            line-height: 24px;
        }
    }
}

.radar-update-button {
    // Empuja el botón al fondo del viewport si no hay scroll; si lo hay, queda tras los módulos.
    // z-index por encima del empty state (z-index: 10 + pointer-events: all).
    margin-top: auto;
    align-self: flex-end;
    flex-shrink: 0;
    position: relative;
    z-index: 11;
}
</style>

<style lang="scss">
.tooltip-container.radar-update-error {
    &.error .andy-mood {
        background-image: img('andy-surprise.svg');
    }

    .close {
        display: none;
    }

    .andy-talk span {
        font-family: $text-bold;
    }

    &:not(.portrait):not(.landscape) {
        .andy-talk {
            min-height: 55px;
        }

        .typed-element {
            width: 100%;
            padding-right: 0;
        }

        .andy-talk span {
            white-space: nowrap;
        }
    }
}
</style>
